import React, { useState, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom'; // Import Link for navigation
import { useAuth } from '../context/AuthContext'; // Use the hook providing the implemented login
import { GoogleLogin, CredentialResponse } from '@react-oauth/google';
import { toast } from 'react-toastify'; // Import toast for notifications
import { useTranslation } from 'react-i18next'; // Import useTranslation

const Login = () => {
    const [credentials, setCredentials] = useState({ email: '', password: '' });
    const [error, setError] = useState<string | null>(null); // Use null for no error state
    const [loading, setLoading] = useState(false); // For email/password login
    const { login, completePhoneOtpLogin } = useAuth(); // Get login and new function
    const navigate = useNavigate();
    const { t } = useTranslation(); // Initialize useTranslation


    // State for Phone OTP Login
    const [phoneNumber, setPhoneNumber] = useState('');
    const [otp, setOtp] = useState('');
    const [otpSent, setOtpSent] = useState(false);
    const [loadingOtpRequest, setLoadingOtpRequest] = useState(false);
    const [loadingOtpLogin, setLoadingOtpLogin] = useState(false);
    const [phoneLoginError, setPhoneLoginError] = useState<string | null>(null);
    const [activeTab, setActiveTab] = useState<'email' | 'phone' | 'google'>('email');

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setCredentials(prev => ({ ...prev, [name]: value }));
    };

    
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null); // Clear previous errors
        setLoading(true);

        try {
            await login(credentials.email, credentials.password);
            // Navigation is now handled by AuthContext's handleSuccessfulLogin
            

        } catch (err: any) {
            console.error("Login page handleSubmit failed:", err);
            let errorMessage = t('login.errors.defaultLoginFailed');

            if (err.response?.status === 401) {
                errorMessage = t('login.errors.invalidCredentials');
            } else if (err.message) {
                errorMessage = err.message;
            }
            setError(errorMessage);

        } finally {
            setLoading(false);
        }
    };


    const handleGoogleLoginSuccess = async (credentialResponse: CredentialResponse) => {
        setError(null);
        setLoading(true);

        if (credentialResponse.credential) {
            const idToken = credentialResponse.credential;
            try {
                await login(idToken, undefined, true); // Pass token, indicate Google login
            } catch (err: any) {
                console.error("Google login processing failed:", err);
                setError(err.message || t('login.errors.googleLoginFailed'));
            } finally {
                setLoading(false);
            }
        } else {
            setError(t('login.errors.googleNoCredential'));
            setLoading(false);
        }
    };


    const handleRequestOtp = async () => {
        setPhoneLoginError(null);
        setLoadingOtpRequest(true);
        try {
            const apiUrl = window.APP_CONFIG?.REACT_APP_API_URL || 'https://t1.u8.my/api';
            const response = await fetch(`${apiUrl}/auth/phone/request-otp`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                },
                body: JSON.stringify({ phone_number: phoneNumber }),
            });
            
            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.message || t('login.errors.otpRequestFailed'));
            }
            setOtpSent(true);
            toast.success(data.message || t('login.otpSentSuccess'));
        } catch (err: any) {
            setPhoneLoginError(err.message || t('login.errors.otpRequestGeneric'));
            toast.error(err.message || t('login.errors.otpRequestFailed'));
        } finally {
            setLoadingOtpRequest(false);
        }
    };

    const handlePhoneOtpLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        setPhoneLoginError(null);
        setLoadingOtpLogin(true);
        try {
            const apiUrl = window.APP_CONFIG?.REACT_APP_API_URL || 'https://t1.u8.my/api';
            const response = await fetch(`${apiUrl}/auth/phone/verify-otp`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                },
                body: JSON.stringify({ phone_number: phoneNumber, otp: Number(otp) }),
            });

            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.message || t('login.errors.otpVerifyFailed'));
            }

            const { access_token, user, rolesPermissions, customer } = data;
            if (!access_token || !user) {
                throw new Error(t('login.errors.missingTokenOrUserData'));
            }
            await completePhoneOtpLogin(access_token, user, rolesPermissions, customer);
        } catch (err: any) {
            setPhoneLoginError(err.message || t('login.errors.otpRequestGeneric'));
            toast.error(err.message || t('login.errors.otpVerifyFailed'));
        } finally {
            setLoadingOtpLogin(false);
        }
    };

    const [otpDigits, setOtpDigits] = useState(['', '', '', '', '', '']);
    const otpInputsRef = useRef<Array<HTMLInputElement | null>>([]);

    const handleOtpChange = (index: number, value: string) => {
        if (!/^\d?$/.test(value)) return;
        const newOtp = [...otpDigits];
        newOtp[index] = value;
        setOtpDigits(newOtp);

        if (value && index < 5) {
            otpInputsRef.current[index + 1]?.focus();
        }
        const joinedOtp = newOtp.join('');
        setOtp(joinedOtp);

        if (index === 5 && value && joinedOtp.length === 6 && joinedOtp.split('').every(d => d !== '')) {
            setTimeout(() => {
                otpInputsRef.current[5]?.form?.requestSubmit?.();
            }, 10);
        }
    };

    const handleOtpKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Backspace' && !otpDigits[index] && index > 0) {
            otpInputsRef.current[index - 1]?.focus();
        }
    };

    const [firstOtpFocused, setFirstOtpFocused] = useState(false);

    const handleFirstOtpFocus = () => {
        if (otpDigits.some(d => d !== '')) {
            setOtpDigits(['', '', '', '', '', '']);
            setOtp('');
            setTimeout(() => otpInputsRef.current[0]?.select(), 0);
        }
        setFirstOtpFocused(true);
    };

    const handleFirstOtpBlur = () => {
        setFirstOtpFocused(false);
    };

    return (
        <div className="container mx-auto p-4 max-w-md mt-8">
            <h1 className="text-2xl font-bold mb-6 text-center">{t('login.title')}</h1>

            <div className="my-4 flex justify-center border-b">
                <button
                    className={`px-4 py-2 text-sm font-medium ${activeTab === 'email' ? 'border-b-2 border-blue-500 text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
                    onClick={() => { setActiveTab('email'); setError(null); setPhoneLoginError(null); }}
                >
                    {t('login.emailTab')}
                </button>
                <button
                    className={`px-4 py-2 text-sm font-medium ${activeTab === 'phone' ? 'border-b-2 border-blue-500 text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
                    onClick={() => { setActiveTab('phone'); setError(null); setPhoneLoginError(null); setOtpSent(false); setOtp(''); }}
                >
                    {t('login.phoneTab')}
                </button>
                <button
                    className={`px-4 py-2 text-sm font-medium ${activeTab === 'google' ? 'border-b-2 border-blue-500 text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
                    onClick={() => { setActiveTab('google'); setError(null); setPhoneLoginError(null); }}
                >
                    {t('login.googleTab')}
                </button>
            </div>

            {activeTab === 'google' && (
                <div className="flex justify-center mt-4">
                    <GoogleLogin
                        onSuccess={handleGoogleLoginSuccess}
                        onError={() => { setError(t('login.errors.googleLoginFailed')); }}
                        useOneTap
                    />
                </div>
            )}

            {activeTab === 'phone' && (
                <form onSubmit={handlePhoneOtpLogin} className="bg-white rounded-lg p-8 pt-2 pb-2">
                    {phoneLoginError && <p className="text-red-500 text-sm mb-4">{phoneLoginError}</p>}
                    {!otpSent ? (
                        <>
                            <div className="mb-4">
                                <label className="block text-gray-700 mb-2" htmlFor="phoneNumber">{t('login.phoneNumberLabel')}</label>
                                <input
                                    type="tel"
                                    id="phoneNumber"
                                    value={phoneNumber}
                                    onChange={(e) => setPhoneNumber(e.target.value)}
                                    required
                                    placeholder={t('login.phoneNumberPlaceholder')}
                                    className="w-full px-3 py-2 border rounded focus:outline-none focus:ring focus:border-blue-300"
                                />
                            </div>
                            <button
                                type="button"
                                onClick={handleRequestOtp}
                                disabled={loadingOtpRequest || !phoneNumber}
                                className="w-full bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded disabled:opacity-50"
                            >
                                {loadingOtpRequest ? t('login.sendingOtp') : t('login.sendOtpButton')}
                            </button>
                        </>
                    ) : (
                        <>
                            <div className="mb-4">
                                <label className="block text-gray-700 mb-2" htmlFor="otp">{t('login.enterOtpLabel', { phoneNumber })}</label>
                                <div className="flex space-x-2 justify-center">
                                    {otpDigits.map((digit, idx) => (
                                        <input
                                            key={idx}
                                            type="text"
                                            inputMode="numeric"
                                            maxLength={1}
                                            value={digit}
                                            ref={el => otpInputsRef.current[idx] = el}
                                            onChange={e => handleOtpChange(idx, e.target.value)}
                                            onKeyDown={e => handleOtpKeyDown(idx, e)}
                                            className="w-12 h-12 text-2xl text-center border rounded focus:outline-none focus:ring focus:border-blue-300"
                                            autoFocus={idx === 0}
                                            onFocus={idx === 0 ? handleFirstOtpFocus : undefined}
                                            onMouseDown={idx === 0 ? (e) => {
                                                if (document.activeElement === otpInputsRef.current[0] && otpDigits.some(d => d !== '')) {
                                                    e.preventDefault();
                                                    setOtpDigits(['', '', '', '', '', '']);
                                                    setOtp('');
                                                    setTimeout(() => otpInputsRef.current[0]?.select(), 0);
                                                }
                                            } : undefined}
                                            onBlur={idx === 0 ? handleFirstOtpBlur : undefined}
                                        />
                                    ))}
                                </div>
                            </div>
                            <button
                                type="submit"
                                disabled={loadingOtpLogin || otp.length !== 6}
                                className="w-full bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded disabled:opacity-50"
                            >
                                {loadingOtpLogin ? t('login.loggingIn') : t('login.loginWithOtpButton')}
                            </button>
                        </>
                    )}
                </form>
            )}

            {activeTab === 'email' && (
                <form onSubmit={handleSubmit} className="bg-white rounded-lg p-8 pb-2">
                    <div className="mb-4">
                        <label className="block text-gray-700 mb-2" htmlFor="email">{t('login.emailLabel')}</label>
                        <input
                            type="email"
                            id="email"
                            name="email"
                            value={credentials.email}
                            onChange={handleChange}
                            required
                            className="w-full px-3 py-2 border rounded focus:outline-none focus:ring focus:border-blue-300"
                            autoComplete="email"
                        />
                    </div>
                    <div className="mb-6">
                        <label className="block text-gray-700 mb-2" htmlFor="password">{t('login.passwordLabel')}</label>
                        <input
                            type="password"
                            id="password"
                            name="password"
                            value={credentials.password}
                            onChange={handleChange}
                            required
                            className="w-full px-3 py-2 border rounded focus:outline-none focus:ring focus:border-blue-300"
                            autoComplete="current-password"
                        />
                    </div>
                    {error && <p className="text-red-500 text-sm mb-4">{error}</p>}
                    <button
                        type="submit"
                        disabled={loading}
                        className="w-full bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded disabled:opacity-50 disabled:cursor-not-allowed transition duration-200"
                    >
                        {loading ? t('login.loggingIn') : t('login.loginButton')}
                    </button>

                    <p className="mt-4 text-center text-sm">
                        {t('login.noAccount')}{' '} <Link to="/register" className="text-blue-500 hover:underline">{t('login.registerLink')}</Link>
                    </p>
                    <p className="mt-2 text-center text-sm">
                        <Link to="/forgot-password" className="text-blue-500 hover:underline">{t('login.forgotPasswordLink')}</Link>
                    </p>
                </form>
            )}
        </div>
    );
};

export default Login;
